import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import PlusGlyph from './PlusGlyph'

interface Props {
    label: string
    to?: string
    onClick?: () => void
    disabled?: boolean
    disabledLabel?: string
    className?: string
}

const CreateFab: React.FC<Props> = ({
    label,
    to,
    onClick,
    disabled = false,
    disabledLabel,
    className,
}) => {
    const content = (
        <>
            <PlusGlyph />
            <span>{disabled ? disabledLabel ?? label : label}</span>
        </>
    )
    return (
        <Host className={className}>
            <Glass>
                {disabled ? (
                    <Pill as='button' type='button' disabled>
                        {content}
                    </Pill>
                ) : to ? (
                    <Pill as={Link} to={to} onClick={onClick}>
                        {content}
                    </Pill>
                ) : (
                    <Pill as='button' type='button' onClick={onClick}>
                        {content}
                    </Pill>
                )}
            </Glass>
        </Host>
    )
}

export default CreateFab

const Host = styled.div`
    display: none;

    @media (max-width: 540px) {
        display: block;
        position: absolute;
        right: 1rem;
        left: auto;
        bottom: max(1.25rem, calc(env(safe-area-inset-bottom) + 0.75rem));
        z-index: 50;
        pointer-events: none;
    }
`

const Glass = styled.div`
    pointer-events: auto;
    display: inline-flex;
    padding: 0.625rem;
    border-radius: 1.75rem;
    background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.18) 0%,
        rgba(255, 255, 255, 0.03) 38%,
        rgba(255, 255, 255, 0.02) 65%,
        rgba(255, 255, 255, 0.08) 100%
    );
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.55),
        inset 0 -0.5px 0 rgba(255, 255, 255, 0.06),
        0 8px 24px rgba(0, 0, 0, 0.18),
        0 24px 56px rgba(0, 0, 0, 0.12);
    backdrop-filter: blur(40px) saturate(180%);
    -webkit-backdrop-filter: blur(40px) saturate(180%);
`

const Pill = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    height: 2.375rem;
    padding: 0 0.875rem;
    border: 0;
    border-radius: 1.0625rem;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.textMobile.lg};
    font-weight: 600;
    letter-spacing: 0.01em;
    text-decoration: none;
    cursor: pointer;
    user-select: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
    transition:
        transform ${({ theme }) => theme.motion.fast} ease,
        background ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
        background: ${({ theme }) => theme.colors.accentDeep};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background: ${({ theme }) => theme.colors.line};
        color: ${({ theme }) => theme.colors.ink3};
        box-shadow: none;
    }
`
